import axios from 'axios';

/**
 * resolveApiBase — pick the right API origin for the current runtime.
 *
 * Why this is non-trivial (May 01 2026 production debug):
 *   - Local dev: frontend on http://localhost:3000, backend on :8001
 *     → must use REACT_APP_BACKEND_URL (no CRA proxy configured).
 *   - Emergent preview: frontend + backend share an origin (the same
 *     `*.preview.emergentagent.com`) so REACT_APP_BACKEND_URL == origin.
 *   - Production custom domain: frontend on https://sharedorigins.org,
 *     backend ingress also on https://sharedorigins.org/api → MUST go
 *     same-origin. REACT_APP_BACKEND_URL was baked at build time and
 *     points to https://tsogn-demo.emergent.host. Calling that
 *     cross-origin from sharedorigins.org with `withCredentials: true`
 *     trips CORS (browser refuses wildcard + credentials, exact-origin
 *     match required). Going same-origin sidesteps all of that.
 *
 * Strategy: if we're in a browser AND on a non-localhost origin that
 * differs from the baked-in env value, prefer the current origin. That
 * lets the platform's ingress route /api to the backend without any
 * CORS dance. Local dev and preview keep their existing REACT_APP_BACKEND_URL
 * behavior because their origin matches (or localhost stays explicit).
 */
function resolveApiBase() {
  const envValue = process.env.REACT_APP_BACKEND_URL || '';
  if (typeof window === 'undefined' || !window.location || !window.location.origin) {
    return envValue;
  }
  const here = window.location.origin;
  if (!envValue) return here; // Build with no env → same-origin.
  if (envValue === here) return envValue; // Preview / matching → same.
  // Cross-origin scenario: keep env backend ONLY for local dev, otherwise
  // prefer the current production origin so the ingress can pass /api
  // through without a credentialed CORS preflight.
  const isLocalHere = /^https?:\/\/(localhost|127\.\d+\.\d+\.\d+|0\.0\.0\.0|192\.168\.|10\.|\[::1\])/i.test(here);
  return isLocalHere ? envValue : here;
}

const API_URL = resolveApiBase();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ─── 401 auto-refresh interceptor ───
// On 401, silently call /auth/refresh once, then retry the original request.
// Refresh cookie is 7 days; access cookie is 60 min. Keeps idle tabs alive
// without forcing a visible re-login.
let refreshInFlight = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const is401 = error.response?.status === 401;
    const isAuthEndpoint = original?.url?.includes('/auth/');
    if (!is401 || original._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      if (!refreshInFlight) {
        refreshInFlight = api.post('/auth/refresh').finally(() => { refreshInFlight = null; });
      }
      await refreshInFlight;
      return api.request(original);
    } catch {
      return Promise.reject(error);
    }
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name) => api.post('/auth/register', { email, password, name }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Chains
export const chainAPI = {
  list: () => api.get('/chains'),
  get: (id) => api.get(`/chains/${id}`),
  create: (data) => api.post('/chains', data),
  update: (id, data) => api.put(`/chains/${id}`, data),
  toggleContradiction: (id) => api.put(`/chains/${id}/toggle-contradiction`),
  delete: (id) => api.delete(`/chains/${id}`),
  setNarrator: (id, voiceId, voiceName) => api.patch(`/chains/${id}/narrator`, { voice_id: voiceId, voice_name: voiceName }),
};

// Narration (ElevenLabs)
// (full definition below)

// Family / My Roots
// (full definition below — with lineage)

// Family / My Roots
// NOTE: full definition below (lineage-aware version with geocoding + progressive beacons)

// AI
export const aiAPI = {
  guide: (message, chainId, sessionId) => api.post('/ai/guide', { message, chain_id: chainId, session_id: sessionId }),
};

// On this day
export const onThisDayAPI = {
  get: () => api.get('/on-this-day'),
};

// Audio
export const audioAPI = {
  list: () => api.get('/audio'),
  upload: (formData) => api.post('/audio', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/audio/${id}`),
};

// Media (Emergent Object Storage)
export const mediaAPI = {
  uploadImage: (formData, onProgress) => api.post('/media/images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  }),
  uploadAudio: (formData, onProgress) => api.post('/media/audio', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  }),
  listChain: (chainId) => api.get(`/media/chains/${chainId}`),
  listAll: (mediaType) => api.get('/media/admin/all', { params: mediaType ? { media_type: mediaType } : {} }),
  delete: (fileId) => api.delete(`/media/${fileId}`),
  fileUrl: (fileId) => `${API_URL}/api/media/file/${fileId}`,
};

// Subscriptions
export const subAPI = {
  tiers: () => api.get('/subscriptions/tiers'),
  checkout: (tierId, originUrl) => api.post('/subscriptions/checkout', { tier_id: tierId, origin_url: originUrl }),
  status: (sessionId) => api.get(`/subscriptions/status/${sessionId}`),
};

// Notes (v2 with sections + citations + versions)
export const noteAPI = {
  list: () => api.get('/notes'),
  get: (id) => api.get(`/notes/${id}`),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.put(`/notes/${id}`, data),
  autosave: (id, data) => api.post(`/notes/${id}/autosave`, data),
  versions: (id) => api.get(`/notes/${id}/versions`),
  delete: (id) => api.delete(`/notes/${id}`),
  fetchCitation: (url) => api.post('/citations/fetch-metadata', { url }),
  sendToGroup: (noteId, groupId) => api.post(`/notes/${noteId}/send-to-group/${groupId}`),
};

// Family / My Roots
export const familyAPI = {
  list: () => api.get('/family'),
  create: (data) => api.post('/family', data),
  update: (id, data) => api.put(`/family/${id}`, data),
  delete: (id) => api.delete(`/family/${id}`),
  geocode: (place) => api.get('/geocode/resolve', { params: { place } }),
};

// Lineage (Build Fest Idea 1)
export const lineageAPI = {
  beacons: () => api.get('/lineage/beacons'),
  intersections: (chainId) => api.get('/lineage/intersections', { params: chainId ? { chain_id: chainId } : {} }),
  getPrivacy: () => api.get('/privacy'),
  setPrivacy: (lineageVisible) => api.patch('/privacy', { lineage_visible: lineageVisible }),
};

// Methodology (moderation explorer + prompts)
export const methodologyAPI = {
  prompts: (stepKey) => api.get('/methodology/prompts', { params: stepKey ? { step_key: stepKey } : {} }),
  modExamples: () => api.get('/methodology/moderation-examples'),
  modFlags: () => api.get('/methodology/moderation-flags'),
  curateExample: (data) => api.post('/methodology/moderation-examples', data),
  removeExample: (id) => api.delete(`/methodology/moderation-examples/${id}`),
};

// Narration (ElevenLabs)
export const narrationAPI = {
  voices: () => api.get('/narration/voices'),
  generate: (chainId, stepKey, voiceId) => api.post('/narration/generate', { chain_id: chainId, step_key: stepKey, voice_id: voiceId }),
  usage: () => api.get('/narration/usage'),
  cachePreviews: () => api.post('/narration/voices/preview-cache'),
  previewStatus: () => api.get('/narration/voices/preview-status'),
  previewUrl: (voiceId) => `${API_URL}/api/narration/voices/preview/${voiceId}`,
  setStepNarrator: (chainId, stepKey, voiceId, voiceName) => api.patch(`/chains/${chainId}/steps/${stepKey}/narrator`, { voice_id: voiceId, voice_name: voiceName }),
};

// Groups (Work Center Mode B)
export const groupAPI = {
  list: () => api.get('/groups'),
  create: (name, description) => api.post('/groups', { name, description }),
  invite: (id, emails) => api.post(`/groups/${id}/invite`, { emails }),
  leave: (id) => api.post(`/groups/${id}/leave`),
  listComments: (id) => api.get(`/groups/${id}/comments`),
  postComment: (id, content, noteId) => api.post(`/groups/${id}/comments`, { content, note_id: noteId }),
  listNotes: (id) => api.get(`/groups/${id}/notes`),
};

// Classrooms (Work Center Mode C)
export const classroomAPI = {
  list: () => api.get('/classrooms'),
  create: (name, description) => api.post('/classrooms', { name, description }),
  invite: (id, emails) => api.post(`/classrooms/${id}/invite`, { emails }),
  listAssignments: (id) => api.get(`/classrooms/${id}/assignments`),
  createAssignment: (id, data) => api.post(`/classrooms/${id}/assignments`, data),
  listSubmissions: (id) => api.get(`/classrooms/${id}/submissions`),
  myAssignments: () => api.get('/assignments/mine'),
  submit: (assignmentId, data) => api.post(`/assignments/${assignmentId}/submit`, data),
  getSubmission: (id) => api.get(`/submissions/${id}`),
  listRatings: (id) => api.get(`/submissions/${id}/ratings`),
  rate: (submissionId, data) => api.post(`/submissions/${submissionId}/rate`, data),
  getVerdict: (submissionId) => api.get(`/submissions/${submissionId}/verdict`),
  submitVerdict: (submissionId, verdict, justification) => api.post(`/submissions/${submissionId}/verdict`, { verdict, justification }),
};

// Accountability — strikes, appeals, audit, moderation
export const accountabilityAPI = {
  myStrikes: () => api.get('/strikes/me'),
  userStrikes: (userId) => api.get(`/strikes/user/${userId}`),
  manualStrike: (userId, reason, sourceRef) => api.post('/strikes/manual', { user_id: userId, reason, source_ref: sourceRef }),
  fileAppeal: (strikeId, statement) => api.post('/appeals', { strike_id: strikeId, statement }),
  myAppeals: () => api.get('/appeals/me'),
  appealQueue: () => api.get('/appeals/queue'),
  voteAppeal: (appealId, decision, notes) => api.post('/appeals/vote', { appeal_id: appealId, decision, notes }),
  myAudit: () => api.get('/audit/me'),
  adminAudit: (params) => api.get('/audit/admin', { params }),
  exportMyData: () => api.get('/audit/export'),
  moderate: (text, surface) => api.post('/moderation/check', { text, surface }),
  getModSettings: () => api.get('/moderation/settings'),
  setShadowMode: (shadowMode) => api.patch('/moderation/settings', { shadow_mode: shadowMode }),
};

// Email (Lineage Digest + governance)
export const emailAPI = {
  getPrefs: () => api.get('/email/prefs'),
  updatePrefs: (prefs) => api.patch('/email/prefs', prefs),
  testSend: () => api.post('/email/test-send'),
  previewUrl: () => `${API_URL}/api/email/preview`,
  batchDigest: () => api.post('/email/batch-digest'),
};

// Account portability — export / cancel / reactivate / delete
export const accountAPI = {
  status: () => api.get('/account/status'),
  exportData: () => api.get('/audit/export'),
  cancel: () => api.post('/account/cancel'),
  reactivate: () => api.post('/account/reactivate'),
  hardDelete: () => api.delete('/account/me'),
};

// Event layer — standalone context anchors (seeded by backend)
export const timelineAPI = {
  events: () => api.get('/timeline/events'),
};

// Parental Controls — PIN + K-12 content filters
export const parentalAPI = {
  get: () => api.get('/parental/settings'),
  setPin: (newPin, currentPin) => api.post('/parental/pin', { new_pin: newPin, current_pin: currentPin || null }),
  verifyPin: (pin) => api.post('/parental/verify-pin', { new_pin: pin }),
  updateFilters: (filters) => api.patch('/parental/filters', filters),
};

// Globe
export const globeAPI = {
  nodes: () => api.get('/globe/nodes'),
  borders: (year) => api.get(`/borders/${year}`),
};

// Lessons
export const lessonAPI = {
  get: (chainId) => api.get(`/lessons/${chainId}`),
};

// Credits
export const creditsAPI = {
  get: () => api.get('/credits'),
  earn: (amount, reason) => api.post('/credits/earn', { amount, reason }),
};

// Rating
export const ratingAPI = {
  get: () => api.get('/rating'),
};

// Launch banner + What's New ticker (Build-Fest → Kickstarter arc)
export const launchAPI = {
  status: () => api.get('/launch/status'),
  adminSetConfig: (data) => api.post('/admin/launch/config', data),
  adminListEntries: () => api.get('/admin/launch/entries'),
  adminCreateEntry: (data) => api.post('/admin/launch/entries', data),
  adminUpdateEntry: (id, data) => api.put(`/admin/launch/entries/${id}`, data),
  adminDeleteEntry: (id) => api.delete(`/admin/launch/entries/${id}`),
};

// Chat
export const chatAPI = {
  rooms: () => api.get('/chat/rooms'),
  createRoom: (name, members, isGroup) => api.post('/chat/rooms', { name, members, is_group: isGroup }),
  messages: (roomId) => api.get(`/chat/rooms/${roomId}/messages`),
  send: (roomId, content, sharedContent) => api.post(`/chat/rooms/${roomId}/messages`, { content, shared_content: sharedContent }),
  react: (messageId, emoji) => api.post(`/chat/messages/${messageId}/react`, { emoji }),
  typing: (roomId) => api.post(`/chat/rooms/${roomId}/typing`),
  getTyping: (roomId) => api.get(`/chat/rooms/${roomId}/typing`),
  unread: (roomId) => api.get(`/chat/rooms/${roomId}/unread`),
};

export function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.message) return detail.message;
  if (detail?.msg) return detail.msg;
  if (typeof detail === 'object') {
    // Structured error with no canonical message field — surface a readable
    // summary instead of "[object Object]".
    try {
      return JSON.stringify(detail);
    } catch {
      return 'Request failed.';
    }
  }
  return String(detail);
}

export default api;
